import React, { CSSProperties, useContext, useEffect, useMemo } from "react";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { useRouteMatch } from "react-router-dom";
import {
  COIN_EXTRA_DETAILS_PATH,
  COIN_EXTRA_DETAILS_TABS,
} from "common/constants";
import TradingViewPriceChart from "features/portfolio/CoinDrawer/TradingViewPriceChart.tsx";
import Title from "common/Typography/Title.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import {
  selectClientToExchangeMapping,
  selectCoinAccountsData,
  selectCoinAccountsPrecision,
  selectUniqueCoinAccountsData,
} from "@shared/slices/portfolio.slice.ts";
import {
  GetCoinExtraExchangesDataRequest,
  LanguageNameSpaces,
} from "@shared/types";
import useMemoizedSelector from "@shared/utils/hooks/useMemoizedSelector.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import { SkipToken } from "@reduxjs/toolkit/query";
import { ExtraDetailsDrawerContext } from "common/ExtraDetails/ExtraDetailsDrawer.context.ts";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon.tsx";
import Row from "antd/es/row";
import Col from "antd/es/col";
import { ExchangesIconsGroup } from "features/portfolio/Assets/ExchangesIconsGroup.tsx";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice.ts";
import ExtraInfoItem from "common/ExtraDetails/ExtraInfoItem.tsx";
import { sumBy } from "lodash";
import { isContractsSymbol } from "@shared/utils/misc.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useTheme } from "styled-components";
import NewLookDetailsGrid from "features/portfolio/NewLook/NewLookDetailsGrid.tsx";

interface AssetGeneralTabProps {
  args: GetCoinExtraExchangesDataRequest | SkipToken;
  isDrawerOpen?: boolean;
}

const AssetGeneralTab = ({ args, isDrawerOpen }: AssetGeneralTabProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { setTabQueries } = useContext(ExtraDetailsDrawerContext) || {};
  const match = useRouteMatch(COIN_EXTRA_DETAILS_PATH);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const clientToExchangeMapping = useSelector(selectClientToExchangeMapping);
  const { isNewLook } = useTheme();

  const matchParams = (match?.params || {}) as {
    coin: string;
    tab: string;
  };
  const coin = matchParams.coin ? decodeURIComponent(matchParams.coin) : "";
  const isSymbol = isContractsSymbol(coin);

  const precisionSelector = useMemo(
    () => selectCoinAccountsPrecision(args),
    [args]
  );

  const accountsData = useMemoizedSelector({
    selectorCreator: selectCoinAccountsData,
    args,
  });
  const uniqueAccountsData = useMemoizedSelector({
    selectorCreator: selectUniqueCoinAccountsData,
    args,
  });

  const getCoinExtraExchangesDataQuery =
    sharedLib.sharedApi.portfolio.useGetCoinExtraExchangesDataQuery(args, {
      skip: !isDrawerOpen,
    });

  useEffect(() => {
    setTabQueries?.(COIN_EXTRA_DETAILS_TABS.GENERAL, [
      getCoinExtraExchangesDataQuery,
    ]);
  }, [getCoinExtraExchangesDataQuery]);

  const accounts = useMemo(
    () =>
      accountsData.filter(
        (account) => !!account.total.USD && account.total.USD > 0
      ),
    [accountsData]
  );

  const uniqueAccounts = useMemo(
    () =>
      uniqueAccountsData.filter(
        (account) => !!account.total.USD && account.total.USD > 0
      ),
    [uniqueAccountsData]
  );

  const accountsNames = useMemo(
    () => accounts.map((account) => account.client),
    [accounts]
  );

  const total = useMemo(
    () =>
      sumBy(uniqueAccounts, (account) => account.total[displayCurrency] || 0),
    [uniqueAccounts, displayCurrency]
  );

  const available = useMemo(
    () =>
      sumBy(
        uniqueAccounts,
        (account) => account.available[displayCurrency] || 0
      ),
    [uniqueAccounts, displayCurrency]
  );

  const exchangeWithMaxTotal = useMemo(() => {
    return uniqueAccountsData
      .slice()
      .sort((a, b) => (b.total.USD || 0) - (a.total.USD || 0))?.[0]?.exchange;
  }, [uniqueAccountsData]);

  const accountsIcons = useMemo(
    () => (
      <ExchangesIconsGroup
        clients={accountsNames}
        clientToExchangeMapping={clientToExchangeMapping}
        style={EXCHANGES_ICONS_STYLE}
        size={28}
      />
    ),
    [accountsNames, clientToExchangeMapping]
  );

  const displayedTotal = useMemo(
    () => (
      <TextWithPrecisionAndIcon
        precisionSelector={precisionSelector}
        text={total}
        round={true}
      />
    ),
    [total, precisionSelector]
  );

  const displayedAvailable = useMemo(
    () => (
      <TextWithPrecisionAndIcon
        precisionSelector={precisionSelector}
        text={available}
        round={true}
      />
    ),
    [available, precisionSelector]
  );

  const accountsTitle = t("assets.accounts");

  const titleToValue = {
    [accountsTitle]: accountsIcons,
    [t("assets.total")]: displayedTotal,
    [t("assets.available")]: displayedAvailable,
  };

  const isLoading = !getCoinExtraExchangesDataQuery.currentData;

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading}
      hideContentWhileLoading={true}
      spinnerSize="large"
    >
      <TradingViewPriceChart
        coin={coin}
        isSymbol={isSymbol}
        defaultExchange={exchangeWithMaxTotal}
        isLoading={isLoading}
      />
      {!isSymbol && isNewLook && (
        <NewLookDetailsGrid
          style={{ marginTop: 16 }}
          title={t("assets.asset_details")}
          columns={3}
          items={Object.entries(titleToValue).map(([title, value]) => ({
            key: title,
            label: title,
            value,
          }))}
        />
      )}
      {!isSymbol && !isNewLook && (
        <>
          <Title
            level={2}
            $weight={typography.fontWeights.SEMI_BOLD}
            style={{ marginBottom: 16 }}
          >
            {t("assets.asset_details")}
          </Title>
          <Row gutter={[16, 24]}>
            {Object.entries(titleToValue).map(([title, value]) => {
              return (
                <Col span={12} key={title}>
                  <ExtraInfoItem
                    name={title}
                    value={value}
                    style={EXTRA_INFO_ITEM_STYLE}
                    ellipsis={true}
                    valueWrapperStyle={
                      title === accountsTitle
                        ? { overflow: "visible" }
                        : undefined
                    }
                  />
                </Col>
              );
            })}
          </Row>
        </>
      )}
    </LoadingSpinnerOverlay>
  );
};

export default React.memo(AssetGeneralTab);

const EXCHANGES_ICONS_STYLE = { margin: 8 };
const EXTRA_INFO_ITEM_STYLE: CSSProperties = {
  flexDirection: "column",
  alignItems: "flex-start",
};
